import { useState } from "react";
import { motion } from "framer-motion";
import { Play, X, Image as ImageIcon, Film } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const mediaItems = [
  // Images - First Lady promotional images only
  {
    id: 1,
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/2u7p9431_98615185-B7AE-4C73-843D-01034639C5FF.png",
    thumbnail: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/2u7p9431_98615185-B7AE-4C73-843D-01034639C5FF.png",
    alt: "First Lady Night Club Logo - VIP Experience Kyrenia Cyprus"
  },
  {
    id: 2,
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/tmtwwu8m_B6A04235-2018-462A-9EC5-6D05826EAAC7.png",
    thumbnail: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/tmtwwu8m_B6A04235-2018-462A-9EC5-6D05826EAAC7.png",
    alt: "First Lady Night Club Performance - Pole Dance Show"
  },
  {
    id: 3,
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/byhqcchk_6DAA2072-66A1-4748-9EE7-2A47009740E6.png",
    thumbnail: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/byhqcchk_6DAA2072-66A1-4748-9EE7-2A47009740E6.png",
    alt: "First Lady Club VIP Lounge Interior"
  },
  {
    id: 4,
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/2eg0xz85_148D7829-3599-423D-BED1-582F512E0182.png",
    thumbnail: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/2eg0xz85_148D7829-3599-423D-BED1-582F512E0182.png",
    alt: "First Lady Night Club Main Hall - Live Show"
  },
  {
    id: 5,
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/lflv90rk_0F609174-6DBE-4AA4-AA07-7CC96608621A.png",
    thumbnail: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/lflv90rk_0F609174-6DBE-4AA4-AA07-7CC96608621A.png",
    alt: "First Lady Night Club Services - VIP Transfer Reservation"
  },
  {
    id: 6,
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/3lnxk1de_C4ED1F5A-9382-457C-A949-2A0F4A5E715E.png",
    thumbnail: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/3lnxk1de_C4ED1F5A-9382-457C-A949-2A0F4A5E715E.png",
    alt: "First Lady Logo - Premium Entertainment Cyprus"
  },
  // Videos - All user uploaded videos
  {
    id: 7,
    type: "video",
    url: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/p8osy1rz_2026-05-26%2005%3A48%3A01.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/2u7p9431_98615185-B7AE-4C73-843D-01034639C5FF.png",
    alt: "First Lady Night Club Promo Video"
  },
  {
    id: 8,
    type: "video",
    url: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/9cpqntc8_2026-05-26%2005%3A39%3A40.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/tmtwwu8m_B6A04235-2018-462A-9EC5-6D05826EAAC7.png",
    alt: "First Lady Club Performance Video"
  },
  {
    id: 9,
    type: "video",
    url: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/g9dw7sz8_2026-05-26%2005%3A42%3A01.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/2eg0xz85_148D7829-3599-423D-BED1-582F512E0182.png",
    alt: "First Lady VIP Experience Video"
  },
  {
    id: 10,
    type: "video",
    url: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/mavjelye_2026-05-26%2005%3A46%3A01.mp4",
    thumbnail: "https://customer-assets.emergentagent.com/job_web-yayini/artifacts/byhqcchk_6DAA2072-66A1-4748-9EE7-2A47009740E6.png",
    alt: "First Lady Night Club Atmosphere"
  }
];

const MediaGallerySection = () => {
  const { t, language } = useLanguage();
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [filter, setFilter] = useState("all");

  const filteredMedia = filter === "all" 
    ? mediaItems 
    : mediaItems.filter(item => item.type === filter);

  // Get translations from context
  const content = {
    subtitle: t("media.subtitle"),
    title1: t("media.title1"),
    title2: t("media.title2"),
    desc: t("media.desc"),
    all: t("media.all"),
    images: t("media.images"),
    videos: t("media.videos")
  };

  return (
    <section id="media" className="py-24 bg-[#0A0A0A] relative overflow-hidden" data-testid="media-gallery-section">
      {/* Background Effects */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[200px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FF00FF]/5 rounded-full blur-[200px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-xs font-mono tracking-[0.3em] text-[#D4AF37] uppercase mb-4 block">
            {content.subtitle}
          </span>
          <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">{content.title1}</span>{" "}
            <span className="text-[#FF00FF]">{content.title2}</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            {content.desc}
          </p>
        </motion.div>

        {/* Filter Buttons */}
        <div className="flex justify-center gap-4 mb-12">
          {[
            { key: "all", label: content.all, icon: null },
            { key: "image", label: content.images, icon: ImageIcon },
            { key: "video", label: content.videos, icon: Film }
          ].map((btn) => (
            <button
              key={btn.key}
              onClick={() => setFilter(btn.key)}
              className={`flex items-center gap-2 px-6 py-3 font-bold text-sm uppercase tracking-wider transition-all ${
                filter === btn.key
                  ? "bg-[#D4AF37] text-black"
                  : "bg-white/5 border border-white/10 text-white hover:border-[#D4AF37]/50"
              }`}
              data-testid={`filter-${btn.key}`}
            >
              {btn.icon && <btn.icon size={16} />}
              {btn.label}
            </button>
          ))}
        </div>

        {/* Media Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredMedia.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="relative aspect-square overflow-hidden group cursor-pointer"
              onClick={() => setSelectedMedia(item)}
              data-testid={`media-item-${item.id}`}
            >
              <img
                src={item.thumbnail}
                alt={item.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                {item.type === "video" ? (
                  <div className="w-16 h-16 rounded-full bg-[#FF00FF] flex items-center justify-center">
                    <Play size={32} className="text-white ml-1" />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center">
                    <ImageIcon size={28} className="text-black" />
                  </div>
                )}
              </div>

              {/* Video badge */}
              {item.type === "video" && (
                <div className="absolute top-2 right-2 bg-[#FF00FF] px-2 py-1 flex items-center gap-1">
                  <Play size={12} className="text-white" />
                  <span className="text-white text-xs font-bold">VIDEO</span>
                </div>
              )}

              {/* Border */}
              <div className="absolute inset-0 border-2 border-[#D4AF37]/0 group-hover:border-[#D4AF37] transition-colors duration-300" />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedMedia && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#D4AF37] transition-colors z-50"
            onClick={() => setSelectedMedia(null)}
          >
            <X size={32} />
          </button>
          
          <div className="max-w-5xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {selectedMedia.type === "video" ? (
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            ) : (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.alt}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default MediaGallerySection;
